import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { BASE_URL, Profession } from '../services/api';
import { iscoMajorOf } from '../data/isco';

type Props = {
  onSelect: (profession: Profession) => void;
  onSkip: () => void;
};

export default function ProfessionScreen({ onSelect, onSkip }: Props) {
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/professions?q=${encodeURIComponent(query)}&lang=${lang}`);
        const data = await res.json();
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, lang]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.inner}>
        <Text style={styles.title}>{t('profession.title')}</Text>
        <Text style={styles.subtitle}>{t('profession.subtitle')}</Text>

        <TextInput
          style={styles.input}
          placeholder={t('profession.placeholder')}
          placeholderTextColor={colors.gray400}
          value={query}
          onChangeText={setQuery}
          autoFocus
          autoCorrect={false}
        />

        {loading && <ActivityIndicator color={colors.black} style={styles.loader} />}

        <FlatList
          data={results}
          keyExtractor={(item) => String(item.id)}
          keyboardShouldPersistTaps="handled"
          renderItem={({ item }) => (
            <TouchableOpacity style={styles.row} onPress={() => onSelect(item)}>
              <Text style={styles.rowName}>{item.name}</Text>
              <Text style={styles.rowGroup}>{t(`isco.${iscoMajorOf(item.isco_group)}`)}</Text>
            </TouchableOpacity>
          )}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />

        <TouchableOpacity style={styles.skip} onPress={onSkip}>
          <Text style={styles.skipText}>{t('profession.skip')}</Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  inner: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl * 3,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: fontSize.md,
    color: colors.gray600,
    marginBottom: spacing.xl,
  },
  input: {
    fontSize: fontSize.lg,
    color: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  loader: {
    marginVertical: spacing.md,
  },
  row: {
    paddingVertical: spacing.md,
  },
  rowName: {
    fontSize: fontSize.md,
    color: colors.black,
    fontWeight: '500',
  },
  rowGroup: {
    fontSize: fontSize.sm,
    color: colors.gray400,
    marginTop: 2,
  },
  separator: {
    height: 1,
    backgroundColor: colors.gray100,
  },
  skip: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
  },
  skipText: {
    fontSize: fontSize.md,
    color: colors.gray400,
  },
});
